import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import logoImg from "../../assets/images/logo.png";

const PAGE_WIDTH_PX = 1400;
const PAGE_HEIGHT_PX = 990;

const normalizeScript = (raw = "") =>
  String(raw)
    .replace(/\\r\\n|\\n|\\r/g, "\n")
    .replace(/\\'/g, "'")
    .replace(/\\"/g, '"')
    .trim();

const escapeHtml = (str = "") =>
  String(str)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const blobToDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result || "");
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

/**
 * Prefer full-size image over thumbnail for PDF quality.
 */
const toFullSizeImageUrl = (imageUrl = "") => {
  const url = String(imageUrl || "").trim();
  if (!url) return "";
  return url.replace(/\/menu-item-images\/thumb\//g, "/menu-item-images/");
};

/**
 * Load remote image as base64 using browser only (requires CORS on image host).
 */
const resolveImageDataUrl = async (imageUrl = "") => {
  const url = toFullSizeImageUrl(imageUrl);
  if (!url) return "";
  if (url.startsWith("data:")) return url;

  // 1) fetch → blob → data URL
  try {
    const response = await fetch(url, { mode: "cors", credentials: "omit" });
    if (response.ok) {
      const blob = await response.blob();
      if (blob && blob.type.startsWith("image/")) {
        return await blobToDataUrl(blob);
      }
    }
  } catch (e) {
    // continue to Image fallback
  }

  // 2) Image + canvas (also needs CORS)
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth || img.width;
        canvas.height = img.naturalHeight || img.height;
        if (!canvas.width || !canvas.height) {
          resolve("");
          return;
        }
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL("image/jpeg", 0.92));
      } catch (err) {
        console.warn("Image canvas convert failed (likely CORS):", url, err);
        resolve("");
      }
    };
    img.onerror = () => {
      console.warn("Image load failed (likely CORS):", url);
      resolve("");
    };
    // cache-bust helps when a non-CORS cached response exists
    const joiner = url.includes("?") ? "&" : "?";
    img.src = `${url}${joiner}t=${Date.now()}`;
  });
};

/**
 * Convert plain or lightly-escaped coaching script into structured HTML.
 */
const formatCoachingScriptHtml = (raw = "") => {
  const text = normalizeScript(raw);
  if (!text) return "<p style='color:#888;'>No coaching script available.</p>";

  // Already HTML from AI formatting
  if (/<\/?(strong|b|br|p|ul|li|div|em)\b/i.test(text)) {
    return text
      .replace(/\n/g, "<br/>")
      .replace(/•\s*/g, "<br/>• ");
  }

  const sections = [];
  const sectionMarkers = ["🍽️", "✨", "🍷", "💬"];
  const markerRegex = /(🍽️|✨|🍷|💬)\s*/g;
  const parts = [];
  let lastIndex = 0;
  let markerMatch;

  while ((markerMatch = markerRegex.exec(text)) !== null) {
    if (markerMatch.index > lastIndex) {
      // Leading text before first marker is ignored for section parsing
    }
    const emoji = markerMatch[1];
    const start = markerMatch.index + markerMatch[0].length;
    const next = sectionMarkers
      .map((m) => {
        const idx = text.indexOf(m, start);
        return idx === -1 ? Infinity : idx;
      })
      .reduce((a, b) => Math.min(a, b), Infinity);
    const end = Number.isFinite(next) ? next : text.length;
    parts.push({ emoji, body: text.slice(start, end).trim() });
    lastIndex = end;
    markerRegex.lastIndex = end;
  }

  parts.forEach(({ emoji, body }) => {
    if (!body) return;

    const bulletParts = body.split("•").map((s) => s.trim()).filter(Boolean);
    const heading = bulletParts.shift() || "";
    const bullets = bulletParts;

    sections.push(`
      <div class="fc-section">
        <div class="fc-section-title">${emoji} <strong>${escapeHtml(heading)}</strong></div>
        ${
          bullets.length
            ? `<ul class="fc-bullets">${bullets
                .map((b) => `<li>${escapeHtml(b)}</li>`)
                .join("")}</ul>`
            : ""
        }
      </div>
    `);
  });

  if (sections.length) return sections.join("");

  // Fallback: show bullets if present
  if (text.includes("•")) {
    const items = text.split("•").map((s) => s.trim()).filter(Boolean);
    return `<ul class="fc-bullets">${items
      .map((b) => `<li>${escapeHtml(b)}</li>`)
      .join("")}</ul>`;
  }

  return `<p>${escapeHtml(text)}</p>`;
};

const waitForImages = (root) => {
  const images = Array.from(root.querySelectorAll("img"));
  return Promise.all(
    images.map(
      (img) =>
        new Promise((resolve) => {
          if (img.complete && img.naturalWidth > 0) {
            resolve();
            return;
          }
          img.onload = () => resolve();
          img.onerror = () => resolve();
        })
    )
  );
};

const buildPageElement = (card, logoSrc, imageDataUrl = "") => {
  const title = escapeHtml(card.flash_card_title || "Flashcard");
  const scriptHtml = formatCoachingScriptHtml(card.format_coching_script || "");

  const page = document.createElement("div");
  page.className = "flashcard-pdf-page";
  page.style.cssText = `
    width: ${PAGE_WIDTH_PX}px;
    height: ${PAGE_HEIGHT_PX}px;
    background: #ffffff;
    box-sizing: border-box;
    padding: 36px 40px 32px;
    font-family: Arial, Helvetica, sans-serif;
    color: #222;
    display: flex;
    flex-direction: column;
    position: relative;
  `;

  page.innerHTML = `
    <div style="display:flex; justify-content:space-between; align-items:flex-start; gap:24px; margin-bottom:28px;">
      <div style="
        flex: 1;
        background: #ececec;
        border-radius: 10px;
        padding: 16px 22px;
        font-size: 26px;
        font-weight: 700;
        line-height: 1.3;
        color: #1f1f1f;
        min-height: 58px;
        display: flex;
        align-items: center;
      ">${title}</div>
      <div style="flex-shrink:0; text-align:right; width: 260px;">
        <img
          src="${logoSrc}"
          alt="PerfectCheck"
          style="width: 240px; height: auto; display:block; margin-left:auto;"
        />
      </div>
    </div>

    <div style="flex:1; display:flex; gap:28px; min-height:0; height:100%;">
      <div style="
        flex: 0 0 46%;
        height: 100%;
        min-height: 720px;
        display:flex;
        align-items:center;
        justify-content:center;
        overflow:hidden;
        box-sizing: border-box;
      ">
        <div style="
          width: 560px;
          height: 560px;
          max-width: 90%;
          max-height: 85%;
          background: #f3f3f3;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
          flex-shrink: 0;
        ">
          ${
            imageDataUrl
              ? `<img
                  src="${imageDataUrl}"
                  alt=""
                  style="
                    width: 100%;
                    height: 100%;
                    object-fit: contain;
                    object-position: center;
                    display: block;
                  "
                />`
              : `<span style="color:#999; font-size:18px;">No image</span>`
          }
        </div>
      </div>

      <div style="
        flex: 1;
        border: 1px solid #e5e5e5;
        border-radius: 14px;
        padding: 22px 26px;
        overflow: hidden;
        background: #fff;
      ">
        <div class="fc-script-content" style="
          font-size: 17px;
          line-height: 1.45;
          color: #222;
        ">
          ${scriptHtml}
        </div>
      </div>
    </div>

    <style>
      .fc-section { margin-bottom: 16px; }
      .fc-section-title {
        font-size: 18px;
        margin-bottom: 8px;
        color: #111;
      }
      .fc-bullets {
        margin: 0;
        padding-left: 22px;
      }
      .fc-bullets li {
        margin-bottom: 6px;
      }
      .fc-script-content strong,
      .fc-script-content b {
        font-weight: 700;
      }
    </style>
  `;

  return page;
};

/**
 * Build one landscape PDF with 1 flashcard per page.
 */
export const exportFlashCardsToPDF = async (flashCards = [], fileName = "FlashCards.pdf") => {
  if (!Array.isArray(flashCards) || flashCards.length === 0) {
    throw new Error("No flashcards available to export");
  }

  const pdf = new jsPDF("landscape", "mm", "a4");
  const pdfWidth = pdf.internal.pageSize.getWidth();
  const pdfHeight = pdf.internal.pageSize.getHeight();

  const host = document.createElement("div");
  host.style.cssText = `
    position: fixed;
    left: -10000px;
    top: 0;
    width: ${PAGE_WIDTH_PX}px;
    z-index: -1;
    pointer-events: none;
  `;
  document.body.appendChild(host);

  try {
    for (let i = 0; i < flashCards.length; i++) {
      const card = flashCards[i];
      const imageDataUrl = await resolveImageDataUrl(card.image_name || "");
      const pageEl = buildPageElement(card, logoImg, imageDataUrl);
      host.innerHTML = "";
      host.appendChild(pageEl);

      await waitForImages(pageEl);

      const canvas = await html2canvas(pageEl, {
        scale: 2,
        useCORS: true,
        allowTaint: false,
        backgroundColor: "#ffffff",
        width: PAGE_WIDTH_PX,
        height: PAGE_HEIGHT_PX,
        windowWidth: PAGE_WIDTH_PX,
        windowHeight: PAGE_HEIGHT_PX,
      });

      const imgData = canvas.toDataURL("image/jpeg", 0.92);

      if (i > 0) pdf.addPage();
      pdf.addImage(imgData, "JPEG", 0, 0, pdfWidth, pdfHeight);
    }

    pdf.save(fileName);
  } finally {
    if (host.parentNode) {
      host.parentNode.removeChild(host);
    }
  }
};

export default exportFlashCardsToPDF;
